import React, { useMemo, useState } from 'react';

import axios from 'axios';
import { format, parseISO } from 'date-fns';

import { flattenExpensesTree } from './SupplierContractTermExpensesTable';

import { decimalUpTo } from 'Helpers/currencyFormat';

import EmptyState from 'CommonComponents/EmptyState';
import FormSectionHeading from 'CommonComponents/FormSectionHeading';
import IconButton from 'CommonComponents/IconButton';

import { useToast } from 'Hooks/useToast';

import BoxIcon from 'Assets/icons/box.svg';
import CirclePlusIcon from 'Assets/icons/circle-plus.svg';

// Quantities are stored as decimal(20,4) and entered at any step, so they are shown at the
// precision they were recorded at. Rounding further makes a part-delivery stop reconciling with
// the remainder beside it.
const QUANTITY_DECIMALS = 4;

function formatQuantity(quantity) {
    return decimalUpTo(Number(quantity) || 0, QUANTITY_DECIMALS);
}

/**
 * The server's own message for a failed receipt — a validation error first, then the plain
 * message a 403 or 404 carries. Falling straight to a generic string would report an
 * authorisation failure and a rejected line as the same thing.
 */
function receiptErrorMessage(error) {
    const errors = error?.response?.data?.errors;
    const firstField = errors && Object.keys(errors)[0];

    if (firstField && errors[firstField]?.length) {
        return errors[firstField][0];
    }

    return error?.response?.data?.message || $t('The delivery could not be recorded. Please try again.');
}

/**
 * One row per value line, in the order the Value tab lists them. Lines without a committed
 * expenditure id — packs, pack children, projects, an Unallocated sub-line — are listed but cannot
 * be receipted against: they carry no contracted quantity of their own.
 */
function buildRows(trees) {
    return trees.flatMap((tree, treeIndex) =>
        flattenExpensesTree(tree?.departments).map((row) => ({
            ...row,
            key: `t${treeIndex}-${row.key}`,
        })),
    );
}

export default function SupplierContractTermGoodsReceivedTable({
    expensesTree,
    expensesTrees,
    ownerName,
    canRecord = false,
    storeUrl,
    recorded = {},
    onRecorded,
}) {
    const toast = useToast();

    // Quantity typed against each line, keyed by committed expenditure id.
    const [drafts, setDrafts] = useState({});
    // Per line, not one id: two lines can be in flight at once, and a single id would unlock
    // whichever row was not the one that just resolved while its draft still held a quantity.
    const [submittingIds, setSubmittingIds] = useState(() => new Set());

    const rows = useMemo(() => buildRows(expensesTrees?.length ? expensesTrees : [expensesTree]), [expensesTree, expensesTrees]);

    const receiptableCount = useMemo(() => rows.filter((row) => row.line.committedExpenditureId != null).length, [rows]);

    const recordReceipt = (line) => {
        const id = line.committedExpenditureId;
        const quantity = parseFloat(drafts[id]);

        if (submittingIds.has(id)) {
            return;
        }

        if (!Number.isFinite(quantity) || quantity <= 0) {
            toast.error($t('Enter a quantity greater than zero to record a delivery.'));

            return;
        }

        setSubmittingIds((state) => new Set(state).add(id));

        axios
            .post(storeUrl, { committed_expenditure_id: id, quantity })
            .then((response) => {
                const receipt = response.data?.data ?? {};

                onRecorded(id, {
                    quantity: parseFloat(response.data?.received_quantity) || 0,
                    recordedBy: receipt.user,
                    recordedAt: receipt.recorded_at,
                });
                setDrafts((state) => ({ ...state, [id]: '' }));
                toast.success($t('Goods received recorded'));
            })
            .catch((error) => {
                toast.error(receiptErrorMessage(error));
            })
            .finally(() => {
                setSubmittingIds((state) => {
                    const next = new Set(state);
                    next.delete(id);

                    return next;
                });
            });
    };

    if (!receiptableCount) {
        return (
            <div className="tw-flex tw-flex-col tw-gap-4">
                <GoodsReceivedHeading ownerName={ownerName} />
                <EmptyState
                    className="tw-py-10"
                    icon={BoxIcon}
                    heading={$t('Nothing to receipt on this contract')}
                    body={$t('Goods received is recorded against expenditure value lines. This contract has none yet.')}
                />
            </div>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <GoodsReceivedHeading ownerName={ownerName} />
            {!canRecord && (
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">
                    {$t('Only the contract owner can record a delivery. You can see what has been received so far.')}
                </p>
            )}
            <div className="inbud-table_wrapper tw-max-h-[calc(70vh-2.5rem)] 2xl:tw-max-h-[calc(50vh-2.5rem)] tw-overflow-y-auto">
                <table className="inbud-table tw-table-fixed">
                    <thead className="tw-sticky tw-top-0 tw-z-10">
                        <tr>
                            <th className="tw-w-64">{$t('Line')}</th>
                            <th className="tw-w-36">{$t('Contracted')}</th>
                            <th className="tw-w-36">{$t('Received')}</th>
                            <th className="tw-w-36">{$t('Remaining')}</th>
                            <th className="tw-w-48">{$t('Record a delivery')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <SupplierContractTermGoodsReceivedTableRow
                                key={row.key}
                                line={row.line}
                                departmentLabel={row.departmentLabel}
                                canRecord={canRecord}
                                draft={drafts[row.line.committedExpenditureId] ?? ''}
                                recorded={recorded[row.line.committedExpenditureId]}
                                isSubmitting={submittingIds.has(row.line.committedExpenditureId)}
                                onDraftChange={(value) => setDrafts((state) => ({ ...state, [row.line.committedExpenditureId]: value }))}
                                onRecord={() => recordReceipt(row.line)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function GoodsReceivedHeading({ ownerName }) {
    return (
        <div>
            <FormSectionHeading heading={$t('Goods received')}></FormSectionHeading>
            <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">
                {ownerName
                    ? $t('Owner {{name}} records deliveries against the contract value lines.', { name: ownerName })
                    : $t('The contract owner records deliveries against the contract value lines.')}
            </p>
        </div>
    );
}

function SupplierContractTermGoodsReceivedTableRow({ line, departmentLabel, canRecord, draft, recorded, isSubmitting, onDraftChange, onRecord }) {
    const isReceiptable = line.committedExpenditureId != null;

    const contracted = line.contractedQuantity || 0;
    // A receipt recorded in this session supersedes what the terms payload was fetched with; it
    // posts on its own, so the payload is not refetched.
    const received = recorded ? recorded.quantity : line.receivedQuantity || 0;
    const recordedBy = recorded ? recorded.recordedBy : line.receivedBy;
    const recordedAt = recorded ? recorded.recordedAt : line.receivedAt;
    // A missing name means something different on each path. In-session the recorder is whoever
    // just clicked; on the payload it is a recorder whose account has since been soft-deleted, and
    // naming the reader there would attribute the delivery to the wrong person.
    const recordedByLabel = recordedBy || (recorded ? $t('you') : $t('a deleted user'));
    // Clamped, so a line later edited down below what has already been received reads 0 rather
    // than a negative outstanding quantity.
    const remaining = Math.max(contracted - received, 0);

    if (!isReceiptable) {
        return (
            <tr>
                <td className="tw-break-words">
                    <div>{line.name}</div>
                    <div className="tw-text-xs tw-text-charcoal-75">{departmentLabel}</div>
                </td>
                <td className="inbud-numeric-cell tw-text-charcoal-50">&mdash;</td>
                <td className="inbud-numeric-cell tw-text-charcoal-50">&mdash;</td>
                <td className="inbud-numeric-cell tw-text-charcoal-50">&mdash;</td>
                <td className="tw-text-sm tw-text-charcoal-75">{$t('Not receiptable')}</td>
            </tr>
        );
    }

    return (
        <tr>
            {/* Fixed layout, so an unbroken item or department name would push its neighbours off
                the table rather than widen its own column. */}
            <td className="tw-break-words">
                <div>{line.name}</div>
                <div className="tw-text-xs tw-text-charcoal-75">{departmentLabel}</div>
            </td>
            <QuantityCell quantity={contracted} />
            <QuantityCell quantity={received} />
            <QuantityCell quantity={remaining} />
            <td>
                {canRecord ? (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <label className="tw-sr-only" htmlFor={`goods-received-${line.committedExpenditureId}`}>
                            {$t('Quantity received for {{name}}', { name: line.name })}
                        </label>
                        <input
                            id={`goods-received-${line.committedExpenditureId}`}
                            className="text-input inbud-numeric-field tw-text-end"
                            type="number"
                            min="0"
                            step="any"
                            value={draft}
                            disabled={isSubmitting}
                            onChange={(event) => onDraftChange(event.target.value)}
                            // Nothing wraps the tabs in a <form>, so Enter has no default to
                            // suppress and cannot reach the term save.
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    onRecord();
                                }
                            }}
                        />
                        <IconButton
                            variant="primary"
                            size="sm"
                            title={$t('Record delivery')}
                            aria-label={$t('Record delivery')}
                            disabled={isSubmitting || !draft}
                            onClick={onRecord}
                        >
                            <CirclePlusIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                        </IconButton>
                    </div>
                ) : (
                    <span className="tw-text-sm tw-text-charcoal-75">&mdash;</span>
                )}
                {recordedAt && (
                    <div className="tw-text-xs tw-text-charcoal-75 tw-mt-1">
                        {$t('Recorded by {{name}} on {{date}}', {
                            name: recordedByLabel,
                            date: format(parseISO(recordedAt), 'd MMM yyyy'),
                        })}
                    </div>
                )}
            </td>
        </tr>
    );
}

function QuantityCell({ quantity }) {
    return <td className="inbud-numeric-cell">{formatQuantity(quantity)}</td>;
}
